import React, { Fragment, useState } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import Colors from '../../../constants/Colors';
import ProgressBarTile, { ProgressBarDataItem } from './ProgressBarTile';
import DateLabel from '../DateLabel';
import ScrollViewStyled from '../../ScrollViewStyled';

// eslint-disable-next-line no-shadow, no-unused-vars
export enum ProgressBarWidgetSize { MEDIUM, WIDE }

interface ProgressBarWidgetProps {
  title?: string,
  data: ProgressBarDataItem[],
  size?: ProgressBarWidgetSize,
  withDetail?: boolean,
  circular?: boolean,
}

const ProgressBarWidget = ({
  title, data, size = ProgressBarWidgetSize.MEDIUM, withDetail = false, circular = false,
}: ProgressBarWidgetProps) => {
  const broad: boolean = size === ProgressBarWidgetSize.WIDE;
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const height: number = (containerWidth - 72) / 4;
  const renderSingle: boolean = data?.length === 1;
  const renderTwoRows: boolean = data?.length > 3;
  const splitIndex: number = Math.max(3, Math.floor((data.length + 1) / 2));
  const itemStyle = { minWidth: (containerWidth - 136) * 0.32 };

  return <View style={[styles.container, { height }]}
    onLayout={({ nativeEvent: { layout: { width } } }) => setContainerWidth(width)}
  >
    <View>
      <Text style={styles.title}>{title ?? 'Name'}</Text>
      <DateLabel style={styles.date} />
    </View>
    {renderSingle && <ProgressBarTile
      {...{ ...data[0] }}
      {...{ circular, broad }}
      single
      large
    />}
    {!renderSingle && ((withDetail || circular) ? <ScrollViewStyled horizontal>
      <View>
        <View style={styles.list}>
          {data.slice().splice(0, renderTwoRows ? splitIndex : null)
            .map((item: ProgressBarDataItem, index: number) => <Fragment
              key={`progress_bar_item_${index}`}
            >
              {index > 0 && <View style={styles.itemSeparator} />}
              <ProgressBarTile
                {...{ ...item, circular, broad }}
                style={itemStyle}
              />
            </Fragment>)}
        </View>
        {renderTwoRows && <>
          <View style={styles.listSeparator} />
          <View style={styles.list}>
            {data.slice().splice(splitIndex)
              .map((item: ProgressBarDataItem, index: number) => <Fragment
                key={`progress_bar_item_${index}`}
              >
                {index > 0 && <View style={styles.itemSeparator} />}
                <ProgressBarTile
                  {...{ ...item, circular, broad }}
                  style={itemStyle}
                />
              </Fragment>)}
          </View>
        </>}
      </View>
    </ScrollViewStyled> : <ScrollViewStyled horizontal>
      <View style={styles.wrap}>
        {data.map((item: ProgressBarDataItem, index: number) => <ProgressBarTile
          {...{ ...item, broad, index }}
          key={`progress_bar_item_${index}`}
          style={itemStyle}
        />)}
      </View>
    </ScrollViewStyled>)}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: '#FFF',
    paddingVertical: 16,
    paddingHorizontal: 8,
    marginBottom: 40,
    alignSelf: 'flex-start',
    minHeight: 270,
    width: '100%',
  },
  title: {
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    marginBottom: 4,
    marginStart: 8,
  },
  date: {
    marginBottom: 24,
    marginStart: 8,
  },
  list: {
    flexDirection: 'row',
    minWidth: '100%',
    flex: 1,
  },
  listSeparator: {
    marginVertical: 8,
    width: '100%',
    height: 1,
    backgroundColor: Colors.light.hereworksBlue50,
  },
  itemSeparator: {
    width: 1,
    backgroundColor: Colors.light.hereworksBlue50,
  },
  wrap: {
    flexWrap: 'wrap',
    flex: 1,
  },
});

export default ProgressBarWidget;
